import { PA_INSURANCE_STEPS } from '@app/app/esales/personal-accidents/_sections/steps/PA_INSURANCE_STEPS';
import { PaymentMethodType } from '@app/domain/model/PaymentMethodType';
import { PaymentType } from '@app/domain/model/PaymentType';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { array, boolean, number, object, string } from 'yup';
import { emailMatcher } from '@app/app/_validation/matchers/emailMatcher';

export const PurchasePersonalInsuranceSteps = {
    [PA_INSURANCE_STEPS.CHOOSE_INSURANCE]: object({
        personalInsuranceRisk: string().required('profile.validation.fieldRequired'),
    }),
    [PA_INSURANCE_STEPS.BEGIN_DATE]: object({
        beginDate: string().required('profile.validation.fieldRequired'),
        endDate: string().required('profile.validation.fieldRequired'),
    }),
    [PA_INSURANCE_STEPS.POLICYHOLDER]: object({
        policyholderFirstName: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderLastName: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderPersonalCode: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderStreet: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderHouseNumber: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderCity: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderPostalCode: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderCountry: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderPhoneNumber: string()
            .required('auth.validation.invalidPhoneNumber')
            .test('is-valid-phone', 'auth.validation.invalidPhoneNumber', value => {
                if (!value) {
                    return false;
                }
                return isValidPhoneNumber(value);
            }),
        policyholderEmail: string()
            .min(1, 'profile.validation.fieldRequired')
            .matches(emailMatcher, 'profile.validation.invalidEmail')
            .required('profile.validation.fieldRequired'),
        policyholderEmailConfirmation: string()
            .min(1, 'profile.validation.fieldRequired')
            .matches(emailMatcher, 'profile.validation.invalidEmail')
            .required('profile.validation.fieldRequired')
            .test('emails-match', 'eshop.transport.validation.emailsDoNotMatch', function (value) {
                return value === this.parent.policyholderEmail;
            }),
        policyholderIsInsuredPerson: boolean().optional(),
    }),
    [PA_INSURANCE_STEPS.INSURED_PERSONS]: object({
        insuredPersons: array()
            .of(
                object({
                    age: number().required(),
                    isPolicyholder: boolean().required(),
                    name: string()
                        .min(1, 'profile.validation.fieldRequired')
                        .required('profile.validation.fieldRequired'),
                    surname: string()
                        .min(1, 'profile.validation.fieldRequired')
                        .required('profile.validation.fieldRequired'),
                    personalCode: string()
                        .min(1, 'profile.validation.fieldRequired')
                        .required('profile.validation.fieldRequired')
                        .test(
                            'is-valid-personal-code',
                            'eshop.personalAccidents.validation.personalCodeInvalid',
                            value => !!value && parseLithuanianPersonalCode(value).isValid,
                        )
                        .test(
                            'age-matches-personal-code',
                            'eshop.personalAccidents.ageMismatchError',
                            function (value) {
                                if (!value) return true;

                                const parsedData = parseLithuanianPersonalCode(value);
                                if (!parsedData.isValid || parsedData.age === undefined) {
                                    return true;
                                }

                                const { age } = this.parent;
                                return parsedData.age === age;
                            },
                        ),
                }),
            )
            .min(1)
            .required()
            .test('unique-personal-codes', 'eshop.personalAccidents.duplicatePersonalCode', value => {
                if (!value) {
                    return false;
                }
                const personalCodes = value
                    .map(person => person.personalCode)
                    .filter(code => code && code.trim() !== '');
                return new Set(personalCodes).size === personalCodes.length;
            }),
    }),
    [PA_INSURANCE_STEPS.SUMMARY]: object({
        acceptToPrivacyPolicy: boolean()
            .oneOf([true], 'eshop.personalAccidents.validation.mustAcceptPrivacyPolicy')
            .required('eshop.personalAccidents.validation.mustAcceptPrivacyPolicy'),
        acceptToPaRules: boolean()
            .oneOf([true], 'eshop.personalAccidents.validation.mustAcceptPaRules')
            .required('eshop.personalAccidents.validation.mustAcceptPaRules'),
        acceptToMarketingCnl: boolean().optional(),
        acceptToMarketingCl: boolean().optional(),
    }),
    [PA_INSURANCE_STEPS.PAYMENT.OPTIONS]: object({
        paymentType: string()
            .required('profile.validation.fieldRequired')
            .oneOf(
                [PaymentType.Subscription, PaymentType.ManualPayment],
                'eshop.general.validation.invalidPaymentType',
            ),
    }),
    [PA_INSURANCE_STEPS.PAYMENT.METHOD_TYPE]: object({
        paymentMethodType: string()
            .required('profile.validation.fieldRequired')
            .oneOf(
                [PaymentMethodType.Card, PaymentMethodType.Banks],
                'eshop.general.validation.invalidPaymentMethodType',
            ),
        acceptCardAndPaymentProviderRules: boolean().when('paymentMethodType', {
            is: PaymentMethodType.Card,
            then: schema => schema.is([true], 'eshop.general.validation.mustAcceptPaymentProviderRules'),
            otherwise: schema => schema.optional(),
        }),
    }),
    [PA_INSURANCE_STEPS.PAYMENT.METHOD]: object({
        paymentMethod: string().when('paymentMethodType', {
            is: PaymentMethodType.Banks,
            then: schema => schema.required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
    }),
};
